import BasicRecord from "./BasicRecord";

enum PreType {
    PREFETCH = "prefetch",
    PRELOAD = "preload",
    DNS_PREFETCH = "dns-prefetch",
    PRERENDER = "prerender",
}

type OptionAttributes = Partial<HTMLLinkElement>;
type UrlOrUrls = string[] | string;
type OnError = () => void;
type OnSuccess = () => void;

const executedLinks: BasicRecord<Set<string>> = {
    "prefetch": new Set(),
    "preload": new Set(),
    "dns-prefetch": new Set(),
    "prerender": new Set()
};
const validAsAttrValues: BasicRecord<boolean> = {
    audio: true,
    document: true,
    embed: true,
    fetch: true,
    font: true,
    image: true,
    object: true,
    script: true,
    style: true,
    track: true,
    video: true,
    worker: true
};

function isSupported(preType: string) {
    const maybeLink = document.createElement("link");

    return maybeLink.relList && maybeLink.relList.supports && maybeLink.relList.supports(preType);
}

function addLinkToHead(
    preType: PreType,
    options?: OptionAttributes,
    onError?: OnError,
    onSuccess?: OnSuccess
) {
    return function (url: string) {
        const preExecutedLinks = executedLinks[preType];

        if (!url) {
            return;
        }

        if (preExecutedLinks.has(url)) {
            return;
        }

        preExecutedLinks.add(url);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const link: any = document.createElement("link");

        link.rel = preType;
        link.href = url;
        link.onload = onSuccess ?? null;
        link.onerror = onError ?? (() => console.warn(`Link: ${url} could not be ${preType}ed.`));

        for (const key in options) {
            // eslint-disable-next-line no-prototype-builtins
            if (options.hasOwnProperty(key)) {
                link[key] = options[key as keyof OptionAttributes];
            }
        }

        document.head.appendChild(link);
    };
}

function checkAndAddLink({
    preType,
    urlOrUrls,
    onError,
    onSuccess,
    options
}: {
    preType: PreType;
    urlOrUrls: UrlOrUrls;
    onError?: OnError;
    onSuccess?: OnSuccess;
    options?: OptionAttributes;
}) {
    if (!isSupported(preType)) {
        return;
    }

    if (preType === "preload") {
        const as = options?.as ?? "";

        if (!validAsAttrValues[as]) {
            console.warn('rel="preload" must have a valid `as` attribute');
        }
    }

    const addLinkToHeadHandler = addLinkToHead(preType, options, onError, onSuccess);

    if (Array.isArray(urlOrUrls)) {
        urlOrUrls.forEach(addLinkToHeadHandler);
    } else {
        addLinkToHeadHandler(urlOrUrls);
    }
}

function prefetch(
    urlOrUrls: UrlOrUrls,
    options?: OptionAttributes,
    onError?: OnError,
    onSuccess?: OnSuccess
) {
    checkAndAddLink({
        preType: PreType.PREFETCH,
        urlOrUrls,
        onError,
        onSuccess,
        options
    });
}

function dnsPrefetch(
    urlOrUrls: UrlOrUrls,
    options?: OptionAttributes,
    onError?: OnError,
    onSuccess?: OnSuccess
) {
    checkAndAddLink({
        preType: PreType.DNS_PREFETCH,
        urlOrUrls,
        onError,
        onSuccess,
        options
    });
}

function preload(
    urlOrUrls: UrlOrUrls,
    options?: OptionAttributes,
    onError?: OnError,
    onSuccess?: OnSuccess
) {
    checkAndAddLink({
        preType: PreType.PRELOAD,
        urlOrUrls,
        onError,
        onSuccess,
        options
    });
}

function prerender(
    urlOrUrls: UrlOrUrls,
    options?: OptionAttributes,
    onError?: OnError,
    onSuccess?: OnSuccess
) {
    checkAndAddLink({
        preType: PreType.PRERENDER,
        urlOrUrls,
        onError,
        onSuccess,
        options
    });
}

export {
    prefetch,
    preload,
    prerender,
    dnsPrefetch
};
